/**
 * ShipOrderSheet — slide-in panel for confirming order shipment.
 *
 * Designed for Thai SME admins — all copy in Thai, guided step flow.
 * Two modes: จัดส่งทั้งหมด (full) or เลือกสินค้าที่จะจัดส่ง (partial).
 * Slides in from the right (Shopee-style).
 */

import { useState, useEffect, useRef, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  CheckCircle2,
  Package,
  PackageCheck,
  Truck,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useShipOrder, usePartialShipOrder } from "../api/salesOrderHooks";
import { ItemShipPicker, toShipItems } from "./ItemShipPicker";
import type { SalesOrderItem } from "@/lib/erp/types/order";

interface ShipOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderName: string;
  customerName: string;
  items: SalesOrderItem[];
}

export function ShipOrderDialog({
  open,
  onOpenChange,
  salesOrderName,
  customerName,
  items,
}: ShipOrderDialogProps) {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [mode, setMode] = useState<"choose" | "full" | "partial">("choose");
  const [selections, setSelections] = useState<Map<string, number>>(new Map());

  const shipMutation = useShipOrder(salesOrderName);
  const partialShipMutation = usePartialShipOrder(salesOrderName);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPending = shipMutation.isPending || partialShipMutation.isPending;

  const remainingItems = useMemo(() => toShipItems(items), [items]);

  const totalRemainingQty = useMemo(
    () => remainingItems.reduce((sum, item) => sum + (item.qty - item.delivered_qty), 0),
    [remainingItems]
  );

  const selectedQty = useMemo(() => {
    let sum = 0;
    for (const qty of selections.values()) sum += qty;
    return sum;
  }, [selections]);

  const isEffectiveFullShip = useMemo(() => {
    if (selections.size !== remainingItems.length) return false;
    return remainingItems.every((item) => {
      const remaining = item.qty - item.delivered_qty;
      return selections.get(item.name) === remaining;
    });
  }, [selections, remainingItems]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTrackingNumber("");
      setShowSuccess(false);
      setMode(remainingItems.length === 1 ? "full" : "choose");
      setSelections(new Map());
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    }
  }, [open, remainingItems.length]);

  // Auto-skip to "full" if only 1 item
  useEffect(() => {
    if (open && remainingItems.length === 1) {
      setMode("full");
    }
  }, [open, remainingItems.length]);

  const handleSuccess = (message: string) => {
    setShowSuccess(true);
    toast.success(message);
    autoCloseTimer.current = setTimeout(() => {
      onOpenChange(false);
    }, 1800);
  };

  const handleError = (error: Error) => {
    toast.error(error.message || "ไม่สามารถจัดส่งได้ กรุณาลองอีกครั้ง");
  };

  const handleConfirm = () => {
    const tracking = trackingNumber.trim() || undefined;

    if (mode === "full" || isEffectiveFullShip) {
      shipMutation.mutate(
        { trackingNumber: tracking },
        {
          onSuccess: () => handleSuccess("จัดส่งสินค้าเรียบร้อยแล้ว"),
          onError: handleError,
        }
      );
      return;
    }

    const itemsPayload = Array.from(selections.entries()).map(
      ([soDetail, qty]) => {
        const item = remainingItems.find((i) => i.name === soDetail);
        return { item_code: item?.item_code ?? "", qty, so_detail: soDetail };
      }
    );

    if (itemsPayload.length === 0) {
      toast.error("กรุณาเลือกสินค้าอย่างน้อย 1 รายการ");
      return;
    }

    partialShipMutation.mutate(
      { items: itemsPayload, trackingNumber: tracking },
      {
        onSuccess: () =>
          handleSuccess(`จัดส่ง ${selectedQty} จาก ${totalRemainingQty} ชิ้นเรียบร้อย`),
        onError: handleError,
      }
    );
  };

  const canConfirm = mode === "full" || selections.size > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col overflow-y-auto p-0">
        {showSuccess ? (
          <SuccessView />
        ) : mode === "choose" ? (
          <ChooseView
            customerName={customerName}
            remainingItems={remainingItems}
            totalRemainingQty={totalRemainingQty}
            onSelectFull={() => setMode("full")}
            onSelectPartial={() => setMode("partial")}
          />
        ) : (
          <ShipView
            mode={mode}
            customerName={customerName}
            remainingItems={remainingItems}
            totalRemainingQty={totalRemainingQty}
            selectedQty={selectedQty}
            selections={selections}
            setSelections={setSelections}
            trackingNumber={trackingNumber}
            setTrackingNumber={setTrackingNumber}
            isPending={isPending}
            canConfirm={canConfirm}
            isEffectiveFullShip={isEffectiveFullShip}
            onBack={() => {
              if (remainingItems.length > 1) {
                setMode("choose");
                setSelections(new Map());
              }
            }}
            onConfirm={handleConfirm}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Success view — centered checkmark, auto-closes
// ---------------------------------------------------------------------------

function SuccessView() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
      <div className="h-16 w-16 rounded-2xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center">
        <CheckCircle2 className="h-8 w-8 text-green-500" />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold">จัดส่งเรียบร้อย</p>
        <p className="text-sm text-muted-foreground mt-1">
          ใบส่งสินค้าถูกสร้างแล้ว
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Choose view — pick full or partial (only shown when >1 item)
// ---------------------------------------------------------------------------

function ChooseView({
  customerName,
  remainingItems,
  totalRemainingQty,
  onSelectFull,
  onSelectPartial,
}: {
  customerName: string;
  remainingItems: { name: string; item_name: string; image?: string; qty: number; delivered_qty: number }[];
  totalRemainingQty: number;
  onSelectFull: () => void;
  onSelectPartial: () => void;
}) {
  return (
    <>
      {/* Header with customer + summary */}
      <div className="px-6 pt-6 pb-4 border-b border-border/40">
        <SheetHeader className="mb-0 p-0">
          <SheetTitle className="text-base">จัดส่งสินค้า</SheetTitle>
        </SheetHeader>
        <p className="text-sm text-muted-foreground mt-1">
          ส่งให้ <span className="font-medium text-foreground">{customerName}</span>
        </p>

        {/* Summary pill */}
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
          <Package className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm">
            {remainingItems.length} รายการ &middot; {totalRemainingQty} ชิ้น รอจัดส่ง
          </span>
        </div>
      </div>

      {/* Two mode cards */}
      <div className="flex-1 px-6 py-5 space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          เลือกวิธีจัดส่ง
        </p>

        {/* Full ship */}
        <button
          type="button"
          onClick={onSelectFull}
          className="w-full rounded-xl border border-border hover:border-primary/40 hover:bg-primary/[0.02] transition-colors p-4 text-left group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
              <PackageCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">จัดส่งทั้งหมด</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                ส่งสินค้าครบทุกรายการในครั้งเดียว
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
          </div>
        </button>

        {/* Partial ship */}
        <button
          type="button"
          onClick={onSelectPartial}
          className="w-full rounded-xl border border-border hover:border-primary/40 hover:bg-primary/[0.02] transition-colors p-4 text-left group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center shrink-0">
              <Truck className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">จัดส่งบางรายการ</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                เลือกสินค้าและจำนวนที่ต้องการส่ง
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
          </div>
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ship view — item list + tracking + confirm (for both full & partial)
// ---------------------------------------------------------------------------

function ShipView({
  mode,
  customerName,
  remainingItems,
  totalRemainingQty,
  selectedQty,
  selections,
  setSelections,
  trackingNumber,
  setTrackingNumber,
  isPending,
  canConfirm,
  isEffectiveFullShip,
  onBack,
  onConfirm,
}: {
  mode: "full" | "partial";
  customerName: string;
  remainingItems: { name: string; item_code: string; item_name: string; qty: number; delivered_qty: number; image?: string; rate: number }[];
  totalRemainingQty: number;
  selectedQty: number;
  selections: Map<string, number>;
  setSelections: (s: Map<string, number>) => void;
  trackingNumber: string;
  setTrackingNumber: (v: string) => void;
  isPending: boolean;
  canConfirm: boolean;
  isEffectiveFullShip: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const isFull = mode === "full";
  const showBack = remainingItems.length > 1;

  const confirmLabel = !isFull && selections.size > 0 && !isEffectiveFullShip
    ? `จัดส่ง ${selectedQty} จาก ${totalRemainingQty} ชิ้น`
    : "ยืนยันจัดส่ง";

  return (
    <>
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border/40">
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors -ml-1 mr-1"
            >
              &larr;
            </button>
          )}
          <SheetHeader className="mb-0 p-0 flex-1">
            <SheetTitle className="text-base">
              {isFull ? "จัดส่งทั้งหมด" : "เลือกสินค้าที่จะจัดส่ง"}
            </SheetTitle>
          </SheetHeader>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          ส่งให้ <span className="font-medium text-foreground">{customerName}</span>
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Step indicator */}
        <div className="flex items-center gap-3">
          <StepBadge step={1} active />
          <span className="text-xs font-medium">
            {isFull ? "ตรวจสอบรายการ" : "เลือกสินค้า"}
          </span>
        </div>

        {/* Items section */}
        {isFull ? (
          <div className="space-y-2">
            {remainingItems.map((item) => {
              const remaining = item.qty - item.delivered_qty;
              return (
                <div
                  key={item.name}
                  className="flex items-center gap-3 rounded-xl border p-3"
                >
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-muted flex items-center justify-center">
                    {item.image ? (
                      <img src={item.image} alt={item.item_name} className="h-full w-full object-cover" />
                    ) : (
                      <Package className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.item_name}</p>
                    <p className="text-xs text-muted-foreground">
                      จำนวน {remaining} ชิ้น
                      {item.delivered_qty > 0 && (
                        <> &middot; ส่งแล้ว {item.delivered_qty}</>
                      )}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-muted-foreground">
                    &times;{remaining}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              เลือกสินค้าที่ต้องการจัดส่ง แล้วปรับจำนวนได้
            </p>
            <ItemShipPicker
              items={remainingItems}
              selections={selections}
              onSelectionsChange={setSelections}
              disabled={isPending}
            />
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-border/40" />

        {/* Tracking number */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <StepBadge step={2} active={canConfirm} />
            <span className="text-xs font-medium">เลขพัสดุ (ไม่บังคับ)</span>
          </div>
          <Input
            id="tracking-number"
            placeholder="กรอกเลขพัสดุ เช่น TH123456789"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            disabled={isPending}
            className="rounded-lg"
          />
          <p className="text-[11px] text-muted-foreground">
            ใส่ทีหลังได้ ถ้ายังไม่มีเลขพัสดุ
          </p>
        </div>
      </div>

      {/* Footer */}
      <SheetFooter className="px-6 pb-6 pt-3 border-t border-border/40">
        <Button
          onClick={onConfirm}
          disabled={isPending || !canConfirm}
          className="w-full h-11 rounded-xl text-sm font-semibold"
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              กำลังจัดส่ง...
            </>
          ) : (
            confirmLabel
          )}
        </Button>
      </SheetFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step badge — numbered circle for visual guidance
// ---------------------------------------------------------------------------

function StepBadge({ step, active }: { step: number; active: boolean }) {
  return (
    <div
      className={cn(
        "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {step}
    </div>
  );
}
